import React from 'react';
import PropTypes from 'prop-types';
import {observer} from 'mobx-react';

import autobind from 'common/decorators/autobind.js';
import {elementHasRightOverflow} from 'core/utils.js';
import events from 'events.js';
import {PracticeStoreContext} from 'core/stores/RootStore.js';
import Tools from 'core/components/Tools';
import {
    PAGE_TYPE_PRACTICE_LOCATION,
    PAGE_TYPE_PRACTICE_GROUP,
} from 'core/constants.js';

@observer
class Tabs extends React.Component {
    static contextType = PracticeStoreContext;
    static propTypes = {
        parentRef: PropTypes.oneOfType([
            PropTypes.Object,
            PropTypes.shape({current: PropTypes.instanceOf(Element)}),
        ]),
    };

    constructor(props) {
        super(props);
        this.tabsRef = React.createRef();
    }

    componentDidMount() {
        this.scrollToTab(
            document.getElementById(`${this.context.activeTab.hash}-tab`),
        );

        this.listenToHashChanges();
        this.listenToWindowResize();

        events.listen(
            window,
            ['resize', 'orientationchange'],
            this.listenToWindowResize,
        );

        if (this.tabsRef?.current) {
            events.listen(
                this.tabsRef.current,
                ['scroll'],
                this.handleTabsScroll,
            );
        }
    }

    componentWillUnmount() {
        events.unlisten(
            window,
            ['resize', 'orientationchange'],
            this.listenToWindowResize,
        );

        if (this.tabsRef?.current) {
            events.unlisten(
                this.tabsRef.current,
                ['scroll'],
                this.handleTabsScroll,
            );
        }
    }

    listenToHashChanges() {
        events.listen(
            window,
            ['hashchange', 'popstate'],
            this.context.fetchActiveTab,
        );
    }

    @autobind
    listenToWindowResize() {
        this.handleTabsScroll();
        this.context.onWindowResize();
    }

    @autobind
    handleTabsScroll() {
        if (this.tabsRef?.current && this.props.parentRef?.current) {
            const tabs = this.tabsRef.current;
            const overflow = elementHasRightOverflow(tabs);
            this.toggleOverflowClass(overflow);
        }
    }

    toggleOverflowClass(hasOverflow) {
        const cardProfile = this.props.parentRef.current;

        const overflowClass = 'tabs-overflow';

        if (hasOverflow) {
            if (!cardProfile.classList.contains(overflowClass)) {
                cardProfile.classList.toggle(overflowClass);
            }
        } else {
            if (cardProfile.classList.contains(overflowClass)) {
                cardProfile.classList.toggle(overflowClass);
            }
        }
    }

    @autobind
    handleClick(event) {
        this.scrollToTab(event.target);
    }

    // Scrolls tab to middle if it's too far right or left
    scrollToTab(tabEl) {
        // Make sure it doesn't error on IE
        if (tabEl?.parentNode.scrollBy) {
            const {left, width} = tabEl.getBoundingClientRect();
            const scrollByX = left - window.innerWidth / 2 + width / 2; // eslint-disable-line no-mixed-operators
            tabEl.parentNode.scrollBy(scrollByX, 0);
        }
    }

    renderTabTitle(tab) {
        return (
            <>
                {tab.title}
                {tab.count > 0 && (
                    <span className="count">{` (${tab.count})`}</span>
                )}
            </>
        );
    }

    render() {
        const {
            activeTab,
            displayToolsOnPage,
            isPracticeGroup,
            practice,
            tabs,
            rootStore: {paramStore},
        } = this.context;

        return (
            <nav className="tabs" ref={this.tabsRef} role="tablist">
                {tabs.map((tab) => {
                    return (
                        <a
                            aria-controls={`${tab.hash}-panel`}
                            aria-selected={activeTab.hash === tab.hash}
                            href={`#${tab.hash}`}
                            id={`${tab.hash}-tab`}
                            key={tab.hash}
                            onClick={(event) => this.handleClick(event, tab)}
                            role="tab"
                        >
                            {this.renderTabTitle(tab)}
                        </a>
                    );
                })}
                <span />
                {displayToolsOnPage && (
                    <Tools
                        availableCategories={[
                            'Address',
                            'Phone number',
                            'practitioners',
                            'Other',
                        ]}
                        displayName={practice.name}
                        isClient={!!paramStore.isClient}
                        pageType={
                            isPracticeGroup
                                ? PAGE_TYPE_PRACTICE_GROUP
                                : PAGE_TYPE_PRACTICE_LOCATION
                        }
                    />
                )}
            </nav>
        );
    }
}

export default Tabs;
